import Head from "next/head";
function DetailMetaHead({
  riskTargetDate,
  name,
  aiData,
  ticker,
}: {
  riskTargetDate: string;
  name: string;
  aiData: any;
  ticker: string;
}) {
  const targetDate =
    Number(riskTargetDate?.split?.("-")?.[1]) +
    "월" +
    " " +
    Number(riskTargetDate?.split?.("-")?.[2]) +
    "일" +
    " " +
    name +
    " 종목 진단";

  const filteredMomentumExplainData = aiData?.momentumExplainData?.filter(
    (item: any) => !item?.keyword?.includes("없") && item?.description
  );

  const upDatas = filteredMomentumExplainData?.filter(
    (item: any) => item?.momentum_type === "up"
  );
  const downDatas = filteredMomentumExplainData?.filter(
    (item: any) => item?.momentum_type === "down"
  );

  // JSON-LD 구조화된 데이터 생성
  const generateStructuredData = (): string => {
    if (!aiData) return "{}";
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `https://riskweather.io/detail/${ticker}`,
          name: `${name} 주가 정보 및 투자 분석`,
          url: `https://riskweather.io/detail/${ticker}`,
          description: `${name}의 실시간 주가, AI 투자점수, 상승 여력, 핵심 사업 정보 및 투자 리포트를 확인하세요.`,
          inLanguage: "ko-KR",
          isPartOf: {
            "@id": "https://riskweather.io/#website",
          },
        },
        {
          "@type": "InvestmentOrDeposit",
          "@id": `https://riskweather.io/detail/${ticker}`,
          name: `${name}`,
          description: aiData?.itemProfileData?.business_summary,
          provider: {
            "@type": "Organization",
            name: `${name}`,
            url: `https://riskweather.io/detail/${ticker}`,
          },
        },
        {
          "@type": "Organization",
          name: `${name}`,
          description: aiData?.itemProfileData?.business_summary,
          url: `https://riskweather.io/detail/${ticker}`,
          location: {
            "@type": "Place",
            name: ticker.match(/^\d+$/) ? "대한민국" : "미국",
          },
          department: [
            ...(aiData?.itemProfileData?.core_items
              ?.split(",")
              ?.map((item: any) => ({
                "@type": "Organization",
                name: item.trim(),
              })) || []),
          ],
        },
        ...(aiData?.itemProfileData?.latest_updates
          ?.split(",")
          ?.map((item: any) => ({
            "@type": "CreativeWork",
            name: item.trim(),
          })) || []),
        {
          "@type": "Review",
          positiveNotes: [
            ...(upDatas?.map((item: any) => ({
              "@type": "ListItem",
              name: item?.keyword,
              description: item?.description,
            })) || []),
          ],
          negativeNotes: [
            ...(downDatas?.map((item: any) => ({
              "@type": "ListItem",
              name: item?.keyword,
              description: item?.description,
            })) || []),
          ],
        },
      ],
    };

    return JSON.stringify(structuredData, null, 2);
  };

  return (
    <Head>
      <link rel="Icon" href="/images/favicon.png" type="image/x-icon" />
      <title>
        {name} {"| Riskweather"}
      </title>
      <meta
        property="og:image"
        content={`https://riskweather.io/images/DetailThumb.png`}
        key="og:image"
      />
      <meta
        property="og:description"
        content={"투자매력도와 가격전망, 확인해보세요."}
        key="og:description"
      />
      <meta
        name="keywords"
        content={[
          ...upDatas?.map((item: any) => item?.keyword),
          ...downDatas?.map((item: any) => item?.keyword),
        ].join(", ")}
      />
      <meta
        name="description"
        content={`투자매력도와 가격전망, 확인해보세요.`}
      />
      <meta property="og:title" content={targetDate} key="og:title" />
      <meta name="twitter:title" content={targetDate} />
      <meta
        name="twitter:description"
        content={"투자매력도와 가격전망, 확인해보세요."}
      />
      <meta
        name="twitter:image"
        content={`https://riskweather.io/images/DetailThumb.png`}
      />
      <meta name="twitter:card" content={`summary`} />
      {aiData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: generateStructuredData() }}
        />
      )}
    </Head>
  );
}

export default DetailMetaHead;
